import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { MobileNavigation } from '../components/MobileNavigation';
import { Plus, QrCode, Users, PencilSimple, Trash } from '@phosphor-icons/react';
import { LoadingSpinner } from '../components/ui/loading-spinner';
import { toast } from 'sonner';

import { getApiBaseUrl } from '../utils/api';
export function Players() {
  const [loading, setLoading] = useState(true);
  const [players, setPlayers] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [qrCode, setQRCode] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    date_of_birth: '',
    grade: '',
    instagram_handle: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    medical_notes: ''
  });
  const [editFormData, setEditFormData] = useState({
    name: '',
    date_of_birth: '',
    grade: '',
    instagram_handle: '',
    emergency_contact_name: '',
    emergency_contact_phone: ''
  });
  
  useEffect(() => {
    loadPlayers();
  }, []);
  
  const loadPlayers = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/players`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setPlayers(data);
      } else {
        toast.error('Failed to load players');
      }
    } catch (error) {
      console.error('Load players error:', error);
      toast.error('Failed to load players');
    } finally {
      setLoading(false);
    }
  };
  
  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prepare payload, converting grade to integer if provided
    const payload = {
      ...formData,
      grade: formData.grade ? parseInt(formData.grade, 10) : null
    };
    
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/players`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        const newPlayer = await response.json();
        setPlayers([...players, newPlayer]);
        setFormData({
          name: '',
          date_of_birth: '',
          grade: '',
          instagram_handle: '',
          emergency_contact_name: '',
          emergency_contact_phone: '',
          medical_notes: ''
        });
        setShowAddDialog(false);
        toast.success(`${newPlayer.name} has been added successfully!`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add player');
      }
    } catch (error) {
      console.error('Add player error:', error);
      toast.error('Failed to add player');
    }
  };
  
  const showPlayerQR = async (player) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/players/${player.player_id}/qr`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setQRCode(data.qr_code);
        setSelectedPlayer(player);
        setShowQRDialog(true);
      } else {
        toast.error('Failed to generate QR code');
      }
    } catch (error) {
      console.error('QR code error:', error);
      toast.error('Failed to generate QR code');
    }
  };
  
  const openEditDialog = (player) => {
    setSelectedPlayer(player);
    // Format date for input field (YYYY-MM-DD)
    let dobFormatted = '';
    if (player.date_of_birth) {
      const dob = new Date(player.date_of_birth);
      dobFormatted = dob.toISOString().split('T')[0];
    }
    setEditFormData({
      name: player.name || '',
      date_of_birth: dobFormatted,
      grade: player.grade?.toString() || '',
      instagram_handle: player.instagram_handle || '',
      emergency_contact_name: player.emergency_contact_name || '',
      emergency_contact_phone: player.emergency_contact_phone || ''
    });
    setShowEditDialog(true);
  };
  
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPlayer) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/players/${selectedPlayer.player_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: editFormData.name,
          date_of_birth: editFormData.date_of_birth,
          grade: editFormData.grade ? parseInt(editFormData.grade, 10) : null,
          instagram_handle: editFormData.instagram_handle,
          emergency_contact_name: editFormData.emergency_contact_name,
          emergency_contact_phone: editFormData.emergency_contact_phone
        })
      });
      
      if (response.ok) {
        const updatedPlayer = await response.json();
        setPlayers(players.map(p => p.player_id === selectedPlayer.player_id ? updatedPlayer : p));
        setShowEditDialog(false);
        setSelectedPlayer(null);
        toast.success('Player updated successfully');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update player');
      }
    } catch (error) {
      console.error('Update player error:', error);
      toast.error('Failed to update player');
    } finally {
      setSaving(false);
    }
  };
  
  const handleDeletePlayer = async (player) => {
    setDeleting(player.player_id);
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/players/${player.player_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        setPlayers(players.filter(p => p.player_id !== player.player_id));
        toast.success(`${player.name} has been removed`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete player');
      }
    } catch (error) {
      console.error('Delete player error:', error);
      toast.error('Failed to delete player');
    } finally {
      setDeleting(null);
    }
  };
  
  const calculateAge = (birthDate) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    
    return age;
  };
  
  const getGradeGroup = (age) => {
    if (age <= 11) return 'elementary';
    if (age <= 14) return 'middle';
    if (age <= 18) return 'high';
    return null;
  };
  
  const getGradeGroupDisplay = (age) => {
    if (age <= 11) return '3rd-5th Grade (Elementary)';
    if (age <= 14) return '6th-8th Grade (Middle School)';
    if (age <= 18) return '9th-12th Grade (High School)';
    return null;
  };
  
  const getGradeGroupFromGrade = (grade) => {
    if (grade <= 5) return 'elementary';
    if (grade <= 8) return 'middle';
    return 'high';
  };
  
  const getGradeGroupDisplayFromGrade = (grade) => {
    if (grade <= 5) return '3rd-5th Grade (Elementary)';
    if (grade <= 8) return '6th-8th Grade (Middle School)';
    return '9th-12th Grade (High School)';
  };
  
  const getGradeSuffix = (grade) => {
    if (grade === 3) return '3rd';
    if (grade === 4) return '4th';
    if (grade === 5) return '5th';
    if (grade === 6) return '6th';
    if (grade === 7) return '7th';
    if (grade === 8) return '8th';
    if (grade === 9) return '9th';
    if (grade === 10) return '10th';
    if (grade === 11) return '11th';
    if (grade === 12) return '12th';
    return `${grade}th`;
  };
  
  const isEligibleAge = (age) => {
    return age >= 8 && age <= 18;
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingSpinner size="xl" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background pb-24" data-testid="players-page">
      {/* Premium Header */}
      <div className="glass-effect sticky top-0 z-40">
        <div className="px-6 py-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-1">My Team</p>
              <h1 className="text-2xl font-black uppercase tracking-tight">
                {players.length} Player{players.length !== 1 ? 's' : ''}
              </h1>
            </div>
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button className="btn-primary" data-testid="add-player-btn">
                  <Plus size={20} weight="bold" className="mr-2" />
                  Add Player
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border max-h-[85vh] overflow-y-auto sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-heading text-lg">ADD NEW PLAYER</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-3">
                  {/* Row 1: Name */}
                  <div>
                    <Label htmlFor="name" className="text-xs">Full Name</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="mt-1 h-9"
                      data-testid="player-name-input"
                      placeholder="Player's full name"
                    />
                  </div>
                  
                  {/* Row 2: DOB & Grade side by side */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="date_of_birth" className="text-xs">Date of Birth</Label>
                      <Input
                        id="date_of_birth"
                        name="date_of_birth"
                        type="date"
                        value={formData.date_of_birth}
                        onChange={handleInputChange}
                        required
                        className="mt-1 h-9"
                        data-testid="player-dob-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="grade" className="text-xs">Grade</Label>
                      <Select
                        value={formData.grade}
                        onValueChange={(value) => setFormData({...formData, grade: value})}
                      >
                        <SelectTrigger className="mt-1 h-9" data-testid="player-grade-select">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="3">3rd Grade</SelectItem>
                          <SelectItem value="4">4th Grade</SelectItem>
                          <SelectItem value="5">5th Grade</SelectItem>
                          <SelectItem value="6">6th Grade</SelectItem>
                          <SelectItem value="7">7th Grade</SelectItem>
                          <SelectItem value="8">8th Grade</SelectItem>
                          <SelectItem value="9">9th Grade</SelectItem>
                          <SelectItem value="10">10th Grade</SelectItem>
                          <SelectItem value="11">11th Grade</SelectItem>
                          <SelectItem value="12">12th Grade</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {/* Age indicator - compact */}
                  {formData.date_of_birth && (
                    <p className={`text-xs ${isEligibleAge(calculateAge(formData.date_of_birth)) ? 'text-green-500' : 'text-red-500'}`}>
                      Age: {calculateAge(formData.date_of_birth)} years {isEligibleAge(calculateAge(formData.date_of_birth)) ? '✓' : '- Must be 8-18'}
                    </p>
                  )}
                  
                  {/* Instagram Handle */}
                  <div>
                    <Label htmlFor="instagram_handle" className="text-xs">Instagram Handle <span className="text-red-500">*</span></Label>
                    <Input
                      id="instagram_handle"
                      name="instagram_handle"
                      value={formData.instagram_handle}
                      onChange={handleInputChange}
                      required
                      className="mt-1 h-9"
                      data-testid="instagram-handle-input"
                      placeholder="@playername or n/a"
                    />
                  </div>
                  
                  {/* Emergency Contact - 2 columns */}
                  <div className="border-t border-border pt-3">
                    <p className="text-xs font-medium text-muted-foreground mb-2">Emergency Contact</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="emergency_contact_name" className="text-xs">Name</Label>
                        <Input
                          id="emergency_contact_name"
                          name="emergency_contact_name"
                          value={formData.emergency_contact_name}
                          onChange={handleInputChange}
                          required
                          className="mt-1 h-9"
                          data-testid="emergency-contact-name-input"
                          placeholder="Contact name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="emergency_contact_phone" className="text-xs">Phone</Label>
                        <Input
                          id="emergency_contact_phone"
                          name="emergency_contact_phone"
                          type="tel"
                          value={formData.emergency_contact_phone}
                          onChange={handleInputChange}
                          required
                          className="mt-1 h-9"
                          data-testid="emergency-contact-phone-input"
                          placeholder="(555) 123-4567"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Medical Notes - smaller */}
                  <div>
                    <Label htmlFor="medical_notes" className="text-xs">Medical Notes <span className="text-muted-foreground">(optional)</span></Label>
                    <Textarea
                      id="medical_notes"
                      name="medical_notes"
                      value={formData.medical_notes}
                      onChange={handleInputChange}
                      className="mt-1 text-sm"
                      data-testid="medical-notes-input"
                      placeholder="Allergies, conditions, etc."
                      rows={2}
                    />
                  </div>
                  
                  {/* Buttons */}
                  <div className="flex gap-3 pt-1">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowAddDialog(false)}
                      className="flex-1 h-9"
                    >
                      Cancel
                    </Button>
                    <Button type="submit" className="flex-1 h-9 btn-primary" data-testid="save-player-btn">
                      Save Player
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
      
      {/* Players List */}
      <div className="px-4 py-6">
        {players.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="text-center py-12">
              <Users size={48} weight="duotone" className="mx-auto text-muted-foreground mb-4" />
              <h3 className="font-heading text-lg mb-2">NO PLAYERS YET</h3>
              <p className="text-muted-foreground mb-4">
                Add your first player to start managing their basketball sessions.
              </p>
              <Button 
                onClick={() => setShowAddDialog(true)}
                className="btn-primary"
                data-testid="empty-state-add-player-btn"
              >
                <Plus size={20} weight="bold" className="mr-2" />
                Add Your First Player
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4 stagger-children">
            {players.map((player) => (
              <Card key={player.player_id} className="bg-card border-border card-hover">
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-heading text-lg">{player.name}</h3>
                        <span className="text-xs px-2 py-0.5 bg-primary/10 rounded-full text-primary">
                          {player.grade 
                            ? `${getGradeSuffix(player.grade)} Grade` 
                            : (player.grade_group 
                                ? (player.grade_group === 'elementary' ? 'Elementary' : 
                                   player.grade_group === 'middle' ? 'Middle School' : 'High School')
                                : getGradeGroupDisplay(player.age))}
                        </span>
                      </div>
                      <div className="space-y-1 text-sm text-muted-foreground">
                        <p>Age: {player.age} years old</p>
                        {player.instagram_handle && player.instagram_handle.toLowerCase() !== 'n/a' && (
                          <p className="text-pink-400">📷 {player.instagram_handle}</p>
                        )}
                        <p>Emergency: {player.emergency_contact_name}</p>
                        <p>Phone: {player.emergency_contact_phone}</p>
                        {player.medical_notes && (
                          <p className="text-yellow-400">⚠️ Medical Notes: {player.medical_notes}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-row sm:flex-col gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => showPlayerQR(player)}
                        className="flex-1 sm:flex-none sm:w-full"
                        data-testid={`qr-code-btn-${player.player_id}`}
                      >
                        <QrCode size={16} weight="duotone" className="mr-2" />
                        QR
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEditDialog(player)}
                        className="flex-1 sm:flex-none sm:w-full"
                        data-testid={`edit-player-btn-${player.player_id}`}
                      >
                        <PencilSimple size={16} weight="duotone" className="mr-2" />
                        Edit
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 sm:flex-none sm:w-full text-red-500 border-red-500/30 hover:bg-red-500/10"
                            disabled={deleting === player.player_id}
                            data-testid={`delete-player-btn-${player.player_id}`}
                          >
                            {deleting === player.player_id ? (
                              <LoadingSpinner size="sm" />
                            ) : (
                              <>
                                <Trash size={16} weight="duotone" className="mr-2" />
                                Delete
                              </>
                            )}
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-card border-border">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete {player.name}?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will permanently remove {player.name} from your account. 
                              If they have active session registrations, you'll need to cancel those first.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction 
                              onClick={() => handleDeletePlayer(player)}
                              className="bg-red-500 hover:bg-red-600"
                              data-testid={`confirm-delete-${player.player_id}`}
                            >
                              Delete Player
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
      
      {/* QR Code Dialog */}
      <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
        <DialogContent className="bg-card border-border max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-center">
              {selectedPlayer?.name.toUpperCase()}'S QR CODE
            </DialogTitle>
          </DialogHeader>
          <div className="text-center space-y-4">
            {qrCode && (
              <div className="mx-auto w-64 h-64 bg-white p-4 rounded-lg">
                <img src={qrCode} alt="Player QR Code" className="w-full h-full" />
              </div>
            )}
            <p className="text-sm text-muted-foreground">
              Show this QR code to staff for quick check-in at sessions.
            </p>
            <Button 
              onClick={() => setShowQRDialog(false)}
              className="w-full btn-primary"
              data-testid="close-qr-dialog-btn"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Edit Player Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-card border-border max-h-[85vh] overflow-y-auto sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">EDIT PLAYER</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-3">
            {/* Name */}
            <div>
              <Label htmlFor="edit-name" className="text-xs">Full Name</Label>
              <Input
                id="edit-name"
                value={editFormData.name}
                onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                required
                className="mt-1 h-9"
                data-testid="edit-player-name-input"
              />
            </div>
            
            {/* DOB & Grade side by side */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="edit-dob" className="text-xs">Date of Birth</Label>
                <Input
                  id="edit-dob"
                  type="date"
                  value={editFormData.date_of_birth}
                  onChange={(e) => setEditFormData({...editFormData, date_of_birth: e.target.value})}
                  required
                  className="mt-1 h-9"
                  data-testid="edit-player-dob-input"
                />
              </div>
              <div>
                <Label htmlFor="edit-grade" className="text-xs">Grade</Label>
                <Select
                  value={editFormData.grade}
                  onValueChange={(value) => setEditFormData({...editFormData, grade: value})}
                >
                  <SelectTrigger className="mt-1 h-9" data-testid="edit-player-grade-select">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">3rd Grade</SelectItem>
                    <SelectItem value="4">4th Grade</SelectItem>
                    <SelectItem value="5">5th Grade</SelectItem>
                    <SelectItem value="6">6th Grade</SelectItem>
                    <SelectItem value="7">7th Grade</SelectItem>
                    <SelectItem value="8">8th Grade</SelectItem>
                    <SelectItem value="9">9th Grade</SelectItem>
                    <SelectItem value="10">10th Grade</SelectItem>
                    <SelectItem value="11">11th Grade</SelectItem>
                    <SelectItem value="12">12th Grade</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Instagram */}
            <div>
              <Label htmlFor="edit-instagram" className="text-xs">Instagram Handle</Label>
              <Input
                id="edit-instagram"
                value={editFormData.instagram_handle}
                onChange={(e) => setEditFormData({...editFormData, instagram_handle: e.target.value})}
                className="mt-1 h-9"
                data-testid="edit-instagram-input"
                placeholder="@playername or n/a"
              />
            </div>
            
            {/* Emergency Contact - 2 columns */}
            <div className="border-t border-border pt-3">
              <p className="text-xs font-medium text-muted-foreground mb-2">Emergency Contact</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="edit-emergency-name" className="text-xs">Name</Label>
                  <Input
                    id="edit-emergency-name"
                    value={editFormData.emergency_contact_name}
                    onChange={(e) => setEditFormData({...editFormData, emergency_contact_name: e.target.value})}
                    required
                    className="mt-1 h-9"
                    data-testid="edit-emergency-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-emergency-phone" className="text-xs">Phone</Label>
                  <Input
                    id="edit-emergency-phone"
                    type="tel"
                    value={editFormData.emergency_contact_phone}
                    onChange={(e) => setEditFormData({...editFormData, emergency_contact_phone: e.target.value})}
                    required
                    className="mt-1 h-9"
                    data-testid="edit-emergency-phone-input"
                  />
                </div>
              </div>
            </div>
            
            {/* Buttons */}
            <div className="flex gap-3 pt-1">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowEditDialog(false)}
                className="flex-1 h-9"
              >
                Cancel
              </Button>
              <Button type="submit" className="flex-1 h-9 btn-primary" disabled={saving} data-testid="save-edit-player-btn">
                {saving ? <LoadingSpinner size="sm" /> : 'Save Changes'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      
      <MobileNavigation userRole="parent" />
    </div>
  );
}